import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    serviceNeeded: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('propertyType', formData.propertyType);
      formDataToSend.append('serviceNeeded', formData.serviceNeeded);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d652saijj9tn0ksglm1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyType: '',
          serviceNeeded: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Info */}
          <div>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Get Your <span className="text-teal-600">Free Estimate</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Ready to protect your home from water damage? Contact us today for a free, no-obligation estimate. Our experts will assess your property and provide a customized solution.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-8">
              <a
                href="tel:530-277-9803"
                className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all border border-slate-100 cursor-pointer group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-slate-600 font-medium">Call Us Now</div>
                  <div className="text-xl font-bold text-slate-900">(530) 277-9803</div>
                </div>
              </a>

              <a
                href="mailto:RRGC03@gmail.com"
                className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all border border-slate-100 cursor-pointer group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-slate-600 font-medium">Email Us</div>
                  <div className="text-base font-bold text-slate-900">RRGC03@gmail.com</div>
                </div>
              </a>

              <div className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-md border border-slate-100">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <div className="text-sm text-slate-600 font-medium">Service Area</div>
                  <div className="text-base font-bold text-slate-900">Grass Valley, CA</div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl p-6 border border-teal-200">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-shield-check-fill text-3xl text-teal-600"></i>
                <h3 className="text-lg font-bold text-slate-900">Why Choose Us?</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-700">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  Licensed & Insured (License No. 534840)
                </li>
                <li className="flex items-center text-sm text-slate-700">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  A+ BBB Rating
                </li>
                <li className="flex items-center text-sm text-slate-700">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  15+ Years of Experience
                </li>
                <li className="flex items-center text-sm text-slate-700">
                  <i className="ri-check-line text-teal-600 mr-2"></i>
                  Comprehensive Warranties
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <form id="estimate-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="(530) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="propertyType" className="block text-sm font-semibold text-slate-900 mb-2">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                >
                  <option value="">Select property type</option>
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                </select>
              </div>

              <div>
                <label htmlFor="serviceNeeded" className="block text-sm font-semibold text-slate-900 mb-2">
                  Service Needed *
                </label>
                <select
                  id="serviceNeeded"
                  name="serviceNeeded"
                  value={formData.serviceNeeded}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="Basement Waterproofing">Basement Waterproofing</option>
                  <option value="Crawl Space Encapsulation">Crawl Space Encapsulation</option>
                  <option value="Foundation Repair">Foundation Repair</option>
                  <option value="Sump Pump Installation">Sump Pump Installation</option>
                  <option value="French Drains">French Drains & Drainage</option>
                  <option value="Dry-Rot Repair">Dry-Rot Repair</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your waterproofing needs..."
                ></textarea>
                <div className="text-xs text-slate-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-fill mr-2"></i>
                    Request Free Estimate
                  </span>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or call us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
